import React, { useState, useEffect, useCallback } from "react";
import BrandAIChat from "../components/BrandAIChat";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { getDashboardStats } from "../services/api";
import {
  LayoutDashboard,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  TrendingUp,
  Package,
  Calendar,
  ArrowUpRight,
  Loader2,
  Users,
  Award,
  Target,
  Filter,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    to: new Date().toISOString().split("T")[0],
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDashboardStats(dateRange.from, dateRange.to);
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.from, dateRange.to]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading && !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
          {t("dashboard.messages.loading")}
        </p>
      </div>
    );
  }

  const {
    kpis = {
      total: 0,
      pending: 0,
      confirmed: 0,
      cancelled: 0,
      sent_to_delivery: 0,
      delivered: 0,
      deliveredRevenue: 0,
    },
    topProducts = [],
    topCities = [],
    trend = [],
    agentStats = [],
    confirmationChartData = [],
  } = stats || {};

  const isAgent = user?.role === "confirmation";

  const kpiCards = [
    {
      label: t("dashboard.kpis.total_orders"),
      value: kpis.total,
      icon: LayoutDashboard,
      color: "indigo",
    },
    {
      label: t("status.order.pending"),
      value: kpis.pending,
      icon: Clock,
      color: "amber",
    },
    {
      label: t("status.order.confirmed"),
      value: kpis.confirmed,
      icon: CheckCircle2,
      color: "emerald",
    },
    {
      label: t("status.order.cancelled"),
      value: kpis.cancelled,
      icon: XCircle,
      color: "rose",
    },
    {
      label: t("status.order.sent_to_delivery"),
      value: kpis.sent_to_delivery,
      icon: Truck,
      color: "blue",
    },
    {
      label: t("status.delivery.delivered"),
      value: kpis.delivered,
      icon: CheckCircle2,
      color: "green",
    },
  ];

  // Filter out revenue card if gerant
  const filteredKpiCards = user?.role === "gerant" ? kpiCards.filter(c => c.color !== "green_revenue_placeholder") : kpiCards;
  // NOTE: The deliveredRevenue card is a separate block below the grid, I will protect it there.

  const COLORS = {
    delivered: "#10b981",
    confirmed: "#3b82f6",
    pending: "#f59e0b",
    cancelled: "#ef4444",
    returned: "#f97316",
  };

  return (
    <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 pb-20">
      {/* Header & Date Filter */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight">
            {t("dashboard.title")}{" "}
            {stats?.brandName && (
              <span className="text-indigo-600">— {stats.brandName}</span>
            )}
          </h1>
          <p className="text-gray-400 font-medium mt-1 text-xs md:text-sm md:text-gray-500">
            {t("dashboard.subtitle", {
              brand: stats?.brandName || t("dashboard.brand_fallback"),
            })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 md:gap-3 bg-white p-1.5 md:p-2 rounded-xl md:rounded-2xl border border-gray-100 shadow-sm w-full md:w-auto justify-between md:justify-start">
            <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3">
              <Calendar
                size={14}
                className="text-gray-400 md:w-[16px] md:h-[16px]"
              />
              <input
                type="date"
                className="text-[10px] md:text-xs font-bold text-gray-600 focus:outline-none bg-transparent"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange({ ...dateRange, from: e.target.value })
                }
              />
            </div>
            <div className="w-px h-3 md:h-4 bg-gray-100" />
            <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3">
              <input
                type="date"
                className="text-[10px] md:text-xs font-bold text-gray-600 focus:outline-none bg-transparent"
                value={dateRange.to}
                onChange={(e) =>
                  setDateRange({ ...dateRange, to: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        {kpiCards.map((card, i) => (
          <div
            key={i}
            className={`bg-white p-4 md:p-6 rounded-2xl md:rounded-[2rem] border border-${card.color === "green" ? "emerald" : card.color}-100 shadow-sm flex flex-col justify-between hover:scale-[1.02] transition-transform`}
          >
            <div
              className={`p-2.5 md:p-3 bg-${card.color === "green" ? "emerald" : card.color}-50 text-${card.color === "green" ? "emerald" : card.color}-600 rounded-xl md:rounded-2xl w-fit mb-3 md:mb-4`}
            >
              <card.icon size={18} className="md:w-[20px] md:h-[20px]" />
            </div>
            <div>
              <p className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                {card.label}
              </p>
              <p className="text-xl md:text-2xl font-black text-gray-900">
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Delivered Revenue Line - Hidden for Gerant */}
      {user?.role !== "gerant" && (
        <div className="bg-emerald-50/50 border border-emerald-100 p-5 md:p-6 rounded-2xl md:rounded-[2rem] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 shadow-sm">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2.5 md:p-3 bg-emerald-100 text-emerald-600 rounded-xl md:rounded-2xl">
              <TrendingUp size={20} className="md:w-[24px] md:h-[24px]" />
            </div>
            <div>
              <p className="text-[9px] md:text-[10px] font-black text-emerald-600/60 uppercase tracking-widest mb-1">
                {isAgent
                  ? t("dashboard.kpis.my_commission_title", {
                      defaultValue: "Total Commission",
                    })
                  : t("dashboard.revenue.delivered_title")}
              </p>
              <h3 className="text-xl md:text-2xl font-black text-emerald-700">
                {isAgent
                  ? `${(kpis.commission || 0).toLocaleString()} ${t("common.currency")}`
                  : `${(kpis.deliveredRevenue || 0).toLocaleString()} ${t("common.currency")}`}
              </h3>
            </div>
          </div>
          <div className="text-[9px] md:text-[10px] font-bold text-emerald-600/40 uppercase tracking-widest bg-white/50 px-3 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl border border-emerald-100/50">
            {t("dashboard.revenue.based_on_delivered")}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Trend Chart */}
        <div className="lg:col-span-2 bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-6 md:mb-10">
            <div>
              <h3 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">
                {t("dashboard.charts.trend_title")}
              </h3>
              <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">
                {t("dashboard.charts.trend_subtitle")}
              </p>
            </div>
          </div>

          <div className="h-[300px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={trend}
                margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 800 }}
                  dy={5}
                  interval={
                    trend.length > 14 ? Math.floor(trend.length / 7) : 0
                  }
                  tickFormatter={(d) => {
                    const dt = new Date(d + "T00:00:00");
                    return dt.toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "short",
                    });
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 800 }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "20px",
                    border: "none",
                    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                  }}
                  cursor={{ fill: "#f8fafc" }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{
                    paddingTop: "20px",
                    fontSize: "10px",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                />
                <Bar
                  dataKey="delivered"
                  name={t("dashboard.charts.trend_delivered")}
                  fill={COLORS.delivered}
                  radius={[6, 6, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="confirmed"
                  name={t("dashboard.charts.trend_confirmed")}
                  fill={COLORS.confirmed}
                  radius={[6, 6, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="pending"
                  name={t("dashboard.charts.trend_pending")}
                  fill={COLORS.pending}
                  radius={[6, 6, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="cancelled"
                  name={t("dashboard.charts.trend_cancelled")}
                  fill={COLORS.cancelled}
                  radius={[6, 6, 0, 0]}
                  barSize={20}
                />
                <Bar
                  dataKey="returned"
                  name={t("dashboard.agent_performance.statuses.returned", { defaultValue: "Retourné" })}
                  fill={COLORS.returned}
                  radius={[6, 6, 0, 0]}
                  barSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6 md:mb-8">
            <div>
              <h3 className="text-lg font-black text-gray-900 tracking-tight">
                {t("dashboard.top_products.title")}
              </h3>
              <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">
                {t("dashboard.top_products.subtitle")}
              </p>
            </div>
            <TrendingUp
              size={18}
              className="text-indigo-500 md:w-[20px] md:h-[20px]"
            />
          </div>

          <div className="flex-1 space-y-6">
            {topProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                <Package size={40} className="mb-3 opacity-20" />
                <p className="text-[10px] font-black uppercase tracking-widest">
                  {t("dashboard.top_products.no_data")}
                </p>
              </div>
            ) : (
              topProducts.map((product, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between group"
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center font-black text-gray-400 overflow-hidden border border-gray-100 flex-shrink-0">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package size={20} />
                        )}
                      </div>
                      <div className="absolute -top-2 -left-2 w-5 h-5 bg-indigo-600 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-sm">
                        {i + 1}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 line-clamp-1">
                        {product.name}
                      </p>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        {t("dashboard.top_products.orders_count", {
                          count: product.totalOrders,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 text-gray-400 hover:bg-gray-100 transition-all cursor-crosshair">
                    <ArrowUpRight size={14} />
                  </div>
                </div>
              ))
            )}
          </div>

          <button className="mt-6 md:mt-8 w-full py-3 md:py-4 bg-gray-50 hover:bg-gray-100 text-gray-400 font-bold rounded-xl md:rounded-2xl transition-all uppercase tracking-widest text-[9px] md:text-[10px]">
            {t("dashboard.top_products.view_inventory")}
          </button>
        </div>

        {/* Top Cities */}
        <div className="bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6 md:mb-8">
            <div>
              <h3 className="text-lg font-black text-gray-900 tracking-tight">
                {t("dashboard.top_cities.title")}
              </h3>
              <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">
                {t("dashboard.top_cities.subtitle")}
              </p>
            </div>
            <Target
              size={18}
              className="text-indigo-500 md:w-[20px] md:h-[20px]"
            />
          </div>

          <div className="flex-1 space-y-6">
            {topCities.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                <LayoutDashboard size={40} className="mb-3 opacity-20" />
                <p className="text-[10px] font-black uppercase tracking-widest">
                  {t("dashboard.top_cities.no_data")}
                </p>
              </div>
            ) : (
              topCities.map((city, i) => (
                <div key={i} className="flex flex-col group">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center font-black text-xs border border-indigo-100/50">
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">
                          {city.city}
                        </p>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                          {t("dashboard.top_cities.orders_count", {
                            count: city.totalOrders,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-black text-indigo-600">
                        {Math.round(city.deliveryRate * 100)}%
                      </p>
                      <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
                        {t("dashboard.top_cities.delivery_rate")}
                      </p>
                    </div>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full h-1.5 bg-gray-50 rounded-full overflow-hidden border border-gray-100/50">
                    <div
                      className="h-full bg-indigo-500 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.3)] transition-all duration-1000"
                      style={{
                        width: `${Math.round(city.deliveryRate * 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>

          <button className="mt-6 md:mt-8 w-full py-3 md:py-4 bg-gray-50 hover:bg-gray-100 text-gray-400 font-bold rounded-xl md:rounded-2xl transition-all uppercase tracking-widest text-[9px] md:text-[10px]">
            {t("dashboard.top_cities.view_reports")}
          </button>
        </div>
      </div>

      {user?.role === "admin" && (
        <>
          {/* Agents Status Chart */}
          <div className="bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 shadow-sm">
            <div className="flex justify-between items-center mb-6 md:mb-10">
              <div>
                <h3 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">
                  {t("dashboard.agent_performance.chart_title")}
                </h3>
                <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">
                  {t("dashboard.agent_performance.chart_subtitle")}
                </p>
              </div>
            </div>

            <div className="h-[350px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={confirmationChartData}
                  margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 800 }}
                    interval={0}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 800 }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "20px",
                      border: "none",
                      boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                    }}
                    cursor={{ fill: "#f8fafc" }}
                  />
                  <Legend
                    wrapperStyle={{
                      paddingTop: "20px",
                      fontSize: "10px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  />
                  <Bar
                    dataKey="confirmedCount"
                    name={t("dashboard.agent_performance.statuses.confirmed")}
                    fill={COLORS.confirmed}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="pendingCount"
                    name={t("dashboard.agent_performance.statuses.pending")}
                    fill={COLORS.pending}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="deliveredCount"
                    name={t("dashboard.agent_performance.statuses.delivered")}
                    fill={COLORS.delivered}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="cancelledCount"
                    name={t("dashboard.agent_performance.statuses.cancelled")}
                    fill={COLORS.cancelled}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="returnedCount"
                    name={t("dashboard.agent_performance.statuses.returned", { defaultValue: "Retourné" })}
                    fill={COLORS.returned}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Agents Performance Section */}
          <div className="bg-white p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100 shadow-sm">
            <div className="flex justify-between items-center mb-6 md:mb-10">
              <div>
                <h3 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight">
                  {t("dashboard.agent_performance.title")}
                </h3>
                <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">
                  {t("dashboard.agent_performance.subtitle")}
                </p>
              </div>
              <Users
                className="text-indigo-500 md:w-[24px] md:h-[24px]"
                size={20}
              />
            </div>

            <div className="overflow-hidden">
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#10b981]"></div>
                  <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {t("dashboard.agent_performance.statuses.delivered")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#3b82f6]"></div>
                  <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {t("dashboard.agent_performance.statuses.confirmed")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#ef4444]"></div>
                  <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {t("dashboard.agent_performance.statuses.cancelled")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#f97316]"></div>
                  <span className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {t("dashboard.agent_performance.statuses.returned", { defaultValue: "Retourné" })}
                  </span>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-gray-100">
                      <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        {t("dashboard.agent_performance.table.agent")}
                      </th>
                      <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">
                        {t(
                          "dashboard.agent_performance.table.status_breakdown",
                        )}
                      </th>
                      <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">
                        {t("dashboard.agent_performance.table.totals")}
                      </th>
                      <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">
                        {t("dashboard.agent_performance.table.performance")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {agentStats.map((agent, i) => {
                      const data = [
                        {
                          name: "Delivered",
                          value: agent.deliveredCount,
                          color: COLORS.delivered,
                        },
                        {
                          name: "Confirmed",
                          value: agent.confirmedCount,
                          color: COLORS.confirmed,
                        },
                        {
                          name: "Cancelled",
                          value: agent.cancelledCount,
                          color: COLORS.cancelled,
                        },
                        {
                          name: "Retourné",
                          value: agent.returnedCount || 0,
                          color: COLORS.returned,
                        },
                      ];

                      return (
                        <tr
                          key={i}
                          className="group hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-6">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-black text-sm">
                                {i < 3 ? (
                                  <Award
                                    size={18}
                                    className={
                                      i === 0
                                        ? "text-yellow-500"
                                        : i === 1
                                          ? "text-gray-400"
                                          : "text-amber-600"
                                    }
                                  />
                                ) : (
                                  agent.name.charAt(0)
                                )}
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">
                                  {agent.name}
                                </p>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  {t("dashboard.agent_performance.rank", {
                                    count: i + 1,
                                  })}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-6 min-w-[150px]">
                            <div className="flex items-center justify-center">
                              <div className="w-16 h-16">
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={data}
                                      innerRadius={20}
                                      outerRadius={30}
                                      paddingAngle={2}
                                      dataKey="value"
                                      stroke="none"
                                    >
                                      {data.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={entry.color}
                                        />
                                      ))}
                                    </Pie>
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                              <div className="ml-4 space-y-1">
                                {data.map((entry, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2"
                                  >
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: entry.color }}
                                    />
                                    <span className="text-[10px] font-bold text-gray-600">
                                      {entry.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                          <td className="py-6 text-center">
                            <div className="inline-block text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <Target size={12} className="text-gray-400" />
                                <span className="text-xs font-bold text-gray-700">
                                  {t("dashboard.agent_performance.orders", {
                                    count: agent.totalOrders,
                                  })}
                                </span>
                              </div>
                              {user?.role !== "gerant" && (
                                  <div className="flex items-center gap-2">
                                    <TrendingUp
                                      size={12}
                                      className="text-emerald-500"
                                    />
                                    <span className="text-xs font-black text-emerald-600">
                                      {agent.deliveredRevenue?.toLocaleString()}{" "}
                                      {t("common.currency")}
                                    </span>
                                  </div>
                                )}
                            </div>
                          </td>
                          <td className="py-6 text-right">
                            <div className="inline-block text-right space-y-2">
                              <div>
                                <p className="text-lg font-black text-gray-900">
                                  {Math.round(agent.deliveryRate)}%
                                </p>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  {t(
                                    "dashboard.agent_performance.delivery_rate",
                                  )}
                                </p>
                              </div>
                              <div className="border-t border-gray-50 pt-2">
                                <p className="text-lg font-black text-indigo-600">
                                  {Math.round(agent.confirmationRate || 0)}
                                  %
                                </p>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  {t(
                                    "dashboard.agent_performance.confirmation_rate",
                                    { defaultValue: "Taux de Conf." },
                                  )}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {agentStats.map((agent, i) => {
                  const data = [
                    {
                      name: "Delivered",
                      value: agent.deliveredCount,
                      color: COLORS.delivered,
                    },
                    {
                      name: "Confirmed",
                      value: agent.confirmedCount,
                      color: COLORS.confirmed,
                    },
                    {
                      name: "Cancelled",
                      value: agent.cancelledCount,
                      color: COLORS.cancelled,
                    },
                    {
                      name: "Retourné",
                      value: agent.returnedCount || 0,
                      color: COLORS.returned,
                    },
                  ];

                  return (
                    <div
                      key={i}
                      className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center font-black text-sm">
                            {i < 3 ? (
                              <Award
                                size={18}
                                className={
                                  i === 0
                                    ? "text-yellow-500"
                                    : i === 1
                                      ? "text-gray-400"
                                      : "text-amber-600"
                                }
                              />
                            ) : (
                              agent.name.charAt(0)
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-sm">
                              {agent.name}
                            </p>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                              {t("dashboard.agent_performance.rank", {
                                count: i + 1,
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-black text-indigo-600">
                            {Math.round(agent.deliveryRate)}%
                          </p>
                          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
                            {t("dashboard.agent_performance.delivery_rate")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 py-2 border-y border-gray-100/50">
                        <div className="w-12 h-12 shrink-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={data}
                                innerRadius={15}
                                outerRadius={22}
                                paddingAngle={2}
                                dataKey="value"
                                stroke="none"
                              >
                                {data.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                  />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-3 gap-2 flex-1">
                          {data.map((entry, index) => (
                            <div key={index} className="flex flex-col">
                              <div className="flex items-center gap-1">
                                <div
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest truncate">
                                  {entry.name}
                                </span>
                              </div>
                              <span className="text-[10px] font-bold text-gray-700 ml-2.5">
                                {entry.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-between items-center bg-white/50 p-2 rounded-xl border border-gray-100/50">
                        <div className="flex items-center gap-2">
                          <Target size={12} className="text-gray-400" />
                          <span className="text-[10px] font-bold text-gray-700">
                            {agent.totalOrders} Orders
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp size={12} className="text-emerald-500" />
                          <span className="text-[10px] font-black text-emerald-600">
                            {agent.deliveredRevenue?.toLocaleString()} DH
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {agentStats.length === 0 && (
                <div className="text-center py-16 md:py-20 text-gray-400">
                  <Users
                    size={32}
                    className="mx-auto mb-4 opacity-10 md:w-[40px] md:h-[40px]"
                  />
                  <p className="text-[10px] font-black uppercase tracking-widest">
                    {t("dashboard.agent_performance.no_data")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* {stats?.brandId && (
                <BrandAIChat 
                    brandId={stats.brandId} 
                    brandName={stats.brandName || user?.brandName || "My Store"} 
                />
            )} */}
    </div>
  );
};

export default Dashboard;
